/** O pedaço da extensão `EXT_disjoint_timer_query_webgl2` que o medidor usa. */
interface TimerQueryExt {
  readonly TIME_ELAPSED_EXT: number;
  readonly GPU_DISJOINT_EXT: number;
}

/** Consultas em voo no máximo: a GPU devolve o resultado alguns quadros depois. */
const IN_FLIGHT = 6;

/**
 * Tempo de GPU por quadro (Fase 12, Gate A), pela `EXT_disjoint_timer_query_webgl2`: uma consulta
 * `TIME_ELAPSED` envolve tudo o que o quadro manda à GPU (ícones, cena e pós) e o resultado é lido
 * quando fica pronto, sem travar a CPU. Sem a extensão (o WebKit, alguns drivers), `available` é falso
 * e nada é medido: quem lê o relatório vê a ausência, não um zero. Um quadro "disjunto" (troca de
 * energia ou de GPU no meio) é descartado, como a extensão pede.
 */
export class GpuTimer {
  private readonly gl: WebGL2RenderingContext;
  private readonly ext: TimerQueryExt | null;
  private readonly pending: { query: WebGLQuery; epoch: number }[] = [];
  private readonly free: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;
  /** Janela de medida atual (`mark`): consultas de antes dela chegam marcadas como velhas. */
  private epoch = 0;
  /** Quantas das consultas em voo caíram num intervalo disjunto: lidas e descartadas. */
  private discard = 0;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.ext = gl.getExtension("EXT_disjoint_timer_query_webgl2") as TimerQueryExt | null;
  }

  get available(): boolean {
    return this.ext !== null;
  }

  /**
   * Abre uma janela nova (o `perf(true)` do e2e): os resultados das consultas começadas antes chegam a
   * `poll` com `current` falso, mesmo que fiquem prontos depois — não se misturam à janela nova.
   */
  mark(): void {
    this.epoch++;
  }

  /** Começo do quadro. Com consultas demais em voo, este quadro fica sem medida. */
  begin(): void {
    if (!this.ext || this.active || this.pending.length >= IN_FLIGHT) return;
    const query = this.free.pop() ?? this.gl.createQuery();
    if (!query) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, query);
    this.active = query;
  }

  /** Fim do quadro. */
  end(): void {
    if (!this.ext || !this.active) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push({ query: this.active, epoch: this.epoch });
    this.active = null;
  }

  /** Entrega os resultados prontos (ms), do mais antigo ao mais novo; `current`: da janela atual (`mark`). */
  poll(take: (ms: number, current: boolean) => void): void {
    if (!this.ext || this.pending.length === 0) return;
    // O aviso de disjunção vale para tudo o que estava em voo (e zera ao ser lido).
    if (this.gl.getParameter(this.ext.GPU_DISJOINT_EXT) as boolean) this.discard = this.pending.length;
    while (this.pending.length > 0) {
      const { query, epoch } = this.pending[0]!;
      if (!(this.gl.getQueryParameter(query, this.gl.QUERY_RESULT_AVAILABLE) as boolean)) break;
      const ns = this.gl.getQueryParameter(query, this.gl.QUERY_RESULT) as number;
      this.pending.shift();
      this.free.push(query);
      if (this.discard > 0) this.discard--;
      else take(ns / 1e6, epoch === this.epoch);
    }
  }

  dispose(): void {
    if (this.active) this.end();
    for (const query of [...this.pending.map((p) => p.query), ...this.free]) this.gl.deleteQuery(query);
    this.pending.length = 0;
    this.free.length = 0;
    this.discard = 0;
  }
}
